import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { BasketRightTotalCost } from './BookingRightTotalCost';
import { BasketLeadGuestInfo } from './BasketLeadGuestInfo';
import * as AuthSelectors from 'store/modules/auth/selectors';
import { BasketBookingButtons } from './BasketBookingButtons';
import { TermsAndConditions } from 'containers/HotelContainer/TermsAndConditions';
import { BasketTravelAgentInfo } from './BasketTravelAgentInfo';
import { isNil } from 'lodash-es';
import { IBasketBuildL4 } from 'services/BackendApi/types/Basket';
import { ICompany } from 'services/BackendApi';
import { getProposalListRequestAction } from 'store/modules/proposalsList/actions';
import { depositStatementTotalsRequestAction } from 'store/modules/ledger/actions';
import { TCountryCode } from 'interfaces';
import { isOnHold } from './utils';
import { BasketMultiBookingButtons } from './BasketMultiBookingButtons';
import { allBuildsShareCurrency, computeWeightedMargin, getCombinedTotalDisplay } from './multiHelpers';

interface IBasketRightColumnProps {
  basketBuild: IBasketBuildL4 | null;
  selectedBuilds: IBasketBuildL4[];
  setRedirectLocation: React.Dispatch<React.SetStateAction<string | null>>;
  shouldShowLeaveAlert: boolean;
  setShouldShowLeaveAlert: Dispatch<SetStateAction<boolean>>;
  className?: string;
  onPostRemove: () => void;
  onHoldSuccess?: (basketBuild: { uuid: string; }) => void;
  company: ICompany;
  clientCountryCode: TCountryCode | null;
  isMultiBookingEnabled: boolean;
  selectedTaUserUuid: string | null;
}

export const BasketRightColumn: React.FC<IBasketRightColumnProps> = React.memo(
  ({
    basketBuild,
    selectedBuilds,
    setRedirectLocation,
    setShouldShowLeaveAlert,
    onPostRemove,
    company,
    clientCountryCode,
    onHoldSuccess,
    isMultiBookingEnabled,
    selectedTaUserUuid,
  }) => {
    const isMultiMode = isMultiBookingEnabled && selectedBuilds.length > 1;
    const dispatch = useDispatch();
   
    const effectiveBookingBuilder = basketBuild && isOnHold(basketBuild)
      ? basketBuild.initialBuildResponse
      : basketBuild?.latestBookingBuilderResponse;
    
    const canBook = effectiveBookingBuilder?.canBeBooked;

    const isAdmin = useSelector(AuthSelectors.isAdmin);
    const isSr: boolean = useSelector(AuthSelectors.isInternalUser);
    const isRl: boolean = useSelector(AuthSelectors.isRL);
    const shouldShowTAInfo = isAdmin || (isSr && !isRl);
    const [isRequestInProgress, setRequestInProgress] = useState(false);
    const [liveRatePricesModalData, setLiveRatePricesModalData] = useState([]);
    const [isLiveRatePricesModalOpen, setIsLiveRatePricesModalOpen] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const handleLiveRatePricesModalOpen = (value: boolean) => {
      setIsLiveRatePricesModalOpen(value);
    };
    const handleSetLiveRatePricesModalData = (value: any) => {
      setLiveRatePricesModalData(value);
    };

    let selectedBasketWithTaMargin: IBasketBuildL4 | null = null;

    let commissionPercent: number | undefined;
    let totalAmount: number | undefined;
    let commissionAmount: number | undefined;

    if (basketBuild) {
      commissionPercent = parseFloat(basketBuild.taMarginAmount || '0');
      totalAmount = parseFloat(effectiveBookingBuilder?.totals.total || '0');
      commissionAmount = totalAmount > 0 ? (commissionPercent / 100) * totalAmount : 0;

      selectedBasketWithTaMargin = {
        ...basketBuild,
        taMarginAmount: commissionPercent ? commissionPercent.toFixed(5) : undefined,
      };
    }

    useEffect(() => {
      dispatch(getProposalListRequestAction());
      dispatch(depositStatementTotalsRequestAction(company.uuid));
    }, [selectedBasketWithTaMargin?.uuid]);

    useEffect(() => {
      setShowValidation(false);
    }, [basketBuild?.uuid]);

    return (
      <div className="basket-right-column bg-white overflow-x-clip">
        {selectedBuilds.length === 0 && (
          <span className="block bg-green-25 uppercase text-black font-hurmegeometric-sans py-15px text-center w-full mb-30px">
            No item has been selected
          </span>
        )}
        {selectedBuilds.length > 1 && !isMultiMode && (
          <div className="block bg-green-25 text-black font-hurmegeometric-sans py-15px text-center w-full mb-30px">
            <div className={'uppercase'}>{`${selectedBuilds.length} Items selected`}</div>
            <div>Cannot be booked together.</div>
            <div>Please book separately.</div>
          </div>
        )}
        {isMultiMode && (() => {
          const currenciesMatch = allBuildsShareCurrency(selectedBuilds);
          if (!currenciesMatch) {
            return (
              <div className="block bg-red-25 text-red-100 font-hurmegeometric-sans py-15px px-15px text-center w-full mb-30px border border-red-100">
                <div className="uppercase">{`${selectedBuilds.length} Items selected`}</div>
                <div className="mt-[8px] text-[14px]">
                  Multiple selection without same currency is not allowed.
                </div>
              </div>
            );
          }
          const combined = getCombinedTotalDisplay(selectedBuilds);
          const weightedMargin = computeWeightedMargin(selectedBuilds);
          return (
            <div className="block bg-green-25 text-black font-hurmegeometric-sans py-15px px-15px text-center w-full mb-30px">
              <div className="uppercase">{`${selectedBuilds.length} Items selected`}</div>
              <div className="mt-[8px] text-[16px]">
                {combined.isOnRequest
                  ? 'On Request'
                  : `${combined.currency} ${combined.total.toFixed(2)}`}
              </div>
              <div className="mt-[4px] text-[12px]">{`Commission: ${parseFloat(weightedMargin).toFixed(2)}%`}</div>
            </div>
          );
        })()}
        {!isMultiMode && !isNil(basketBuild) && (
          <div className="mb-30px">
            <BasketRightTotalCost basketBuild={basketBuild} commissionAmount={commissionAmount} />
          </div>
        )}
        <BasketLeadGuestInfo
          disabled={!isMultiMode && isNil(basketBuild)}
          setShouldShowLeaveAlert={setShouldShowLeaveAlert}
          showValidation={showValidation}
        />
        {shouldShowTAInfo ? <BasketTravelAgentInfo travelAgent={null} /> : null}

        {isMultiMode ? (
          <BasketMultiBookingButtons
            selectedBuilds={selectedBuilds}
            selectedTaUserUuid={selectedTaUserUuid}
            isRequestInProgress={isRequestInProgress}
            setRequestInProgress={setRequestInProgress}
            setShouldShowLeaveAlert={setShouldShowLeaveAlert}
            setRedirectLocation={setRedirectLocation}
            setShowValidation={setShowValidation}
            canBook={
              allBuildsShareCurrency(selectedBuilds) &&
              selectedBuilds.every(b => (b.latestBookingBuilderResponse ?? b.initialBuildResponse).canBeBooked !== false)
            }
          />
        ) : (
        <BasketBookingButtons
          basketBuild={selectedBasketWithTaMargin}
          isRequestInProgress={isRequestInProgress}
          setShouldShowLeaveAlert={setShouldShowLeaveAlert}
          setRequestInProgress={setRequestInProgress}
          setRedirectLocation={setRedirectLocation}
          canBook={!!canBook}
          isLiveRatePricesModalOpen={isLiveRatePricesModalOpen}
          setIsLiveRatePricesModalOpen={handleLiveRatePricesModalOpen}
          liveRatePricesModalData={liveRatePricesModalData}
          setLiveRatePricesModalData={handleSetLiveRatePricesModalData}
          onPostRemove={onPostRemove}
          company={company}
          clientCountryCode={clientCountryCode}
          onHoldSuccess={onHoldSuccess}
          setShowValidation={setShowValidation}
        />
        )}

        <div className="terms-and-conditions uppercase">
          <TermsAndConditions className="font-hurmegeometric-sans text-brown-100 visited:text-brown-100 inline-block mt-[18px] text-[12px] uppercase underline" />
        </div>
      </div>
    );
  }
);
